import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useConsent } from '../../context/ConsentContext';
import './CookieConsentBanner.css';

const CookieConsentBanner: React.FC = () => {
  const {
    consent,
    region,
    showBanner,
    bannerConfig,
    isLoading,
    acceptAll,
    rejectNonEssential,
    updateConsent,
    openSettings,
    closeSettings,
    showSettingsModal,
  } = useConsent();

  // Local state for settings modal
  const [localConsent, setLocalConsent] = useState(consent);

  // Update local consent when modal opens
  React.useEffect(() => {
    if (showSettingsModal) {
      setLocalConsent(consent);
    }
  }, [showSettingsModal, consent]);

  // Don't render while loading or if banner shouldn't show
  if (isLoading || (!showBanner && !showSettingsModal)) {
    return null;
  }

  const handleSavePreferences = () => {
    updateConsent(localConsent);
  };

  const handleToggle = (type: 'analytics' | 'marketing') => {
    setLocalConsent(prev => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  return (
    <>
      {/* Main Banner */}
      {showBanner && !showSettingsModal && (
        <div className="cookie-consent-banner">
          <div className="cookie-consent-content">
            <div className="cookie-consent-text">
              <h4>{bannerConfig.title}</h4>
              <p>
                {bannerConfig.message}{' '}
                <Link to="/cookies" className="cookie-policy-link">
                  Learn more
                </Link>
              </p>
              {region === 'CA' && (
                <p className="ccpa-notice">
                  <strong>California Residents:</strong> You have the right to opt out of the sale of your personal information.
                  We do not sell your personal data.
                </p>
              )}
            </div>
            <div className="cookie-consent-actions">
              <button className="cookie-btn-primary" onClick={acceptAll}>
                Accept All
              </button>
              <button className="cookie-btn-outline" onClick={openSettings}>
                Customize
              </button>
              {bannerConfig.showRejectAll && (
                <button className="cookie-btn-ghost" onClick={rejectNonEssential}>
                  {region === 'EU' ? 'Reject Non-Essential' : 'Essential Only'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="cookie-settings-overlay" onClick={closeSettings}>
          <div className="cookie-settings-modal" onClick={e => e.stopPropagation()}>
            <div className="cookie-settings-header">
              <h3>Cookie Preferences</h3>
              <button className="cookie-settings-close" onClick={closeSettings}>
                &times;
              </button>
            </div>

            <div className="cookie-settings-body">
              <p className="cookie-settings-intro">
                We use cookies to enhance your browsing experience, serve personalized content,
                and analyze our traffic. Choose which cookies you consent to below.
              </p>

              {/* Essential Cookies */}
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div className="cookie-category-info">
                    <h4>Essential Cookies</h4>
                    <p>Required for the website to function. Cannot be disabled.</p>
                  </div>
                  <div className="cookie-toggle always-on">
                    <span className="toggle-label">Always On</span>
                  </div>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div className="cookie-category-info">
                    <h4>Analytics Cookies</h4>
                    <p>Help us understand how visitors interact with our website by collecting anonymous data.</p>
                  </div>
                  <label className="cookie-toggle-switch">
                    <input
                      type="checkbox"
                      checked={localConsent.analytics}
                      onChange={() => handleToggle('analytics')}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div className="cookie-category-info">
                    <h4>Marketing Cookies</h4>
                    <p>Used to deliver relevant advertisements and track campaign effectiveness.</p>
                  </div>
                  <label className="cookie-toggle-switch">
                    <input
                      type="checkbox"
                      checked={localConsent.marketing}
                      onChange={() => handleToggle('marketing')}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>

              <div className="cookie-settings-footer-note">
                <p>
                  For more information about how we use cookies, please read our{' '}
                  <Link to="/cookies">Cookie Policy</Link> and{' '}
                  <Link to="/privacy">Privacy Policy</Link>.
                </p>
              </div>
            </div>

            <div className="cookie-settings-footer">
              <button className="cookie-btn-ghost" onClick={rejectNonEssential}>
                Reject All
              </button>
              <button className="cookie-btn-primary" onClick={acceptAll}>
                Accept All
              </button>
              <button className="cookie-btn-primary" onClick={handleSavePreferences}>
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsentBanner;
